<script lang="ts">import { stateUi } from "@infinitro/config";
import { isMobile } from "@infinitro/global";
import { CloseIcon } from "@infinitro/icons";
import { fade } from "svelte/transition";
import { lockBodyScroll, unlockBodyScroll } from "./utils/scroll-lock";
import { trackVisualViewport } from "./utils/visual-viewport-tracker";
let { title = null, closeModal, width = "450px", height = "auto", maxWidth = "50vw", minHeight = "auto", maxHeight = "95vh", maxHeightContent = "calc(95vh - 95px)", padding = "", closeButtonBackground = true, noAnimation = false, lightColor = "", scrollDisabled = true, showScrollbar = false, alwaysCentered = false, notFullScreenMobile = false, closeByBackdrop = true, showCloseButton = true, isDark = false, isPurple = false, zIndexOverlay = 9999, zIndexUnderlay = 9998, buttonCloseCustomStyles = "", customBackground = "", customStyle = "", customStyleContent = "", children, titleSlot, aboveModal, extra } = $props();
let modalPanel = $state(undefined);
let backdropPointerDownTarget = $state(null);
let _noAnimLatch = $state(false);
let scrollLocked = false;
// Guarded so the manual unlock in `close()` and the `$effect` cleanup below
// (both of which can fire for the same close) never double-decrement the
// shared, ref-counted body lock.
const releaseScrollLock = () => {
	if (!scrollLocked) return;
	scrollLocked = false;
	unlockBodyScroll();
};
const close = (event) => {
	event?.preventDefault();
	closeModal();
	if (scrollDisabled) releaseScrollLock();
};
const handleBackdropPointerDown = (event) => {
	backdropPointerDownTarget = event.target;
};
const handleBackdropClick = (event) => {
	if (!closeByBackdrop) return;
	const startedInsideModal = modalPanel?.contains(backdropPointerDownTarget);
	const endedOutsideModal = !modalPanel?.contains(event.target);
	if (!startedInsideModal && endedOutsideModal) {
		close(event);
	}
	backdropPointerDownTarget = null;
};
$effect(() => {
	if (scrollDisabled) {
		lockBodyScroll();
		scrollLocked = true;
	}
	return () => {
		releaseScrollLock();
	};
});
$effect(() => {
	return trackVisualViewport(({ offsetTop, height: vh }) => {
		document.documentElement.style.setProperty("--modal-vv-offset", `${offsetTop}px`);
		document.documentElement.style.setProperty("--modal-vh", `${vh}px`);
	});
});
$effect(() => {
	if (noAnimation || zIndexOverlay > 9999) _noAnimLatch = true;
});
const effectiveNoAnimation = $derived(noAnimation || _noAnimLatch);
const useFlat = $derived($stateUi.modal?.modalStyle === "flat");
const useGlass = $derived($stateUi.modal?.modalStyle === "glass");
const useTinted = $derived($stateUi.modal?.modalStyle === "tinted");
const applyBackgroundBlur = $derived($stateUi.modal?.applyBackgroundBlur ?? false);
const useFlatWithBlur = $derived(useFlat && applyBackgroundBlur);
const isMobileFullscreen = $derived($isMobile && !notFullScreenMobile && !alwaysCentered);
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	class={`overlay full-vh ${alwaysCentered ? 'centered' : ''}`}
	onpointerdown={handleBackdropPointerDown}
	onclick={handleBackdropClick}
	style={`z-index: ${zIndexOverlay} !important;`}
>
	<div
		class="overlay-backdrop"
		class:overlay-dim={!applyBackgroundBlur}
		class:overlay-blur={applyBackgroundBlur}
		class:backdrop-glass-mobile={isMobileFullscreen && useGlass}
		class:backdrop-flat-blur-mobile={isMobileFullscreen && applyBackgroundBlur}
		in:fade={{ duration: 200 }}
		out:fade={{ duration: 150 }}
	></div>
	<div class="modal-container" class:vv-offset-tracked={$isMobile}>
		{@render aboveModal?.()}
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<!-- svelte-ignore a11y_no_static_element_interactions -->
		<div
			bind:this={modalPanel}
			class={`center ${alwaysCentered ? 'centered' : ''} ${$isMobile ? (!effectiveNoAnimation ? 'mobile-center mobile-center-animation' : 'mobile-center') : !effectiveNoAnimation ? 'desktop-center-animation' : ''}`}
			class:center-glass={useGlass}
			class:center-blur={applyBackgroundBlur}
			style={`width: ${isMobileFullscreen ? '100vw' : width}; height: ${
				isMobileFullscreen ? 'var(--modal-vh, var(--app-vh, 100dvh))' : height
			}; min-height: ${minHeight}; ${
				!$isMobile ? `max-width: ${maxWidth}; max-height: ${maxHeight};` : ''
			}`}
			onclick={(e) => e.stopPropagation()}
			out:fade|global={{ duration: 150 }}
		>
			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<div
				class:modal-flat={useFlat || useTinted}
				class:modal-glass={useGlass}
				class:modal-tinted={useTinted}
				class:modal-purple={isPurple}
				class:modal-container-blur={applyBackgroundBlur}
				class:modal-surface-on-backdrop={isMobileFullscreen && (useGlass || applyBackgroundBlur)}
				class={`modal ${alwaysCentered ? 'centered' : ''} ${$isMobile && !effectiveNoAnimation ? 'mobile-modal' : ''} ${
					isDark && !useGlass ? 'modal-dark' : ''
				} ${notFullScreenMobile ? 'not-full-mobile-modal' : ''}`}
				style={`width: ${isMobileFullscreen ? '100vw' : width}; height: ${
					isMobileFullscreen ? 'var(--modal-vh, var(--app-vh, 100dvh))' : height
				}; min-height: ${minHeight}; ${
					!$isMobile ? `max-width: ${maxWidth}; max-height: ${maxHeight};` : ''
				} ${
					lightColor
						? `--light-color: ${lightColor};`
						: `--light-color: color-mix(in srgb, var(--color-secondary-700) 50%, transparent); --light-color: rgb(from var(--color-secondary-700) r g b / 0.5);`
				} ${customBackground ? `background-image: ${customBackground};` : ''} ${customStyle};`}
				onclick={(e) => e.stopPropagation()}
			>
				{#if $isMobile && showCloseButton}
					<button
						class="close"
						class:close-flat={useFlat || useTinted}
						class:close-glass={useGlass}
						class:with-background={closeButtonBackground}
						aria-label="Close"
						onclick={close}
						style={buttonCloseCustomStyles}
					>
						<CloseIcon />
					</button>
				{/if}
				<div
					class="content-wrapper"
					style={`padding: ${padding};${minHeight !== 'auto' ? ` min-height: ${minHeight};` : ''}`}
					class:content-wrapper-blur={applyBackgroundBlur}
					class:content-wrapper-flat-blur={useFlatWithBlur}
				>
					{#if title}
						<div class="title-container">
							<h2>{title}</h2>
							{@render titleSlot?.()}
						</div>
					{/if}
					<div
						class="content {showScrollbar && 'content-with-scrollbar'}"
						style={`max-height: ${!$isMobile ? maxHeightContent : 'none'}; ${customStyleContent};`}
					>
						{@render children()}
					</div>
				</div>
			</div>
			{#if !$isMobile && showCloseButton}
				<div class="close-button-container">
					{@render extra?.()}
					<button
						class="close"
						class:close-flat={useFlat || useTinted}
						class:close-glass={useGlass}
						class:with-background={closeButtonBackground}
						aria-label="Close"
						onclick={close}
						style={buttonCloseCustomStyles}
					>
						<CloseIcon />
					</button>
				</div>
			{/if}
		</div>
	</div>
</div>

<style>
	@keyframes slideUp {
		from {
			transform: translateY(100%);
		}
		to {
			transform: translateY(0);
		}
	}

	@keyframes scaleIn {
		from {
			transform: scale(0.9);
		}
		to {
			transform: scale(1);
		}
	}

	div.overlay {
		position: fixed;
		top: 0px;
		left: 0px;
		right: 0px;
		bottom: 0px;
		display: flex;
		height: 100%;
		width: 100%;
		align-items: flex-end;
		justify-content: center;
		overflow-y: auto;
		overflow-x: hidden;
}

	/*
	 * A single backdrop layer inside `.overlay`, sized to it exactly via `inset: 0`,
	 * instead of a second independently-positioned `position: fixed` element — one fixed
	 * box to keep aligned with the screen instead of two. Kept as its own element (rather
	 * than styling `.overlay` directly) so its fade transition doesn't also fade the
	 * modal content, which has its own separate enter/exit animation.
	 */
	div.overlay-backdrop {
		position: absolute;
		inset: 0px;
}

	div.overlay-dim {
		--tw-bg-opacity: 1;
		background-color: rgb(0 0 0 / var(--tw-bg-opacity, 1));
		opacity: 0.7;
}

	div.overlay-blur {
		background: var(--color-highlight-5);
		backdrop-filter: blur(8px);
	}

	/*
	 * On phone, the modal is full-screen and its own surface used to carry the
	 * backdrop-filter (`.modal-glass` / `.modal-flat.modal-container-blur`). That same
	 * element resizes on every keyboard event (`--modal-vh`), which forced a full-screen
	 * blur recompute on each resize — the source of the slow/glitchy resize. Moving the
	 * blur/surface onto this static backdrop (never resized or translated) keeps the same
	 * look while removing that cost. Values are copied verbatim from the corresponding
	 * `.modal-glass` / `.modal-flat.modal-container-blur` rules below; `opacity: 1`
	 * cancels `.overlay-dim`'s 0.7, since these compound selectors win on specificity.
	 * A solid black layer is stacked underneath each theme's own tint/gradient (rather
	 * than relying on that gradient's own alpha) so the page behind is reliably dimmed
	 * regardless of how translucent a given theme's colors are.
	 */
	div.overlay-backdrop.backdrop-glass-mobile {
		opacity: 1;
		background:
			linear-gradient(
				to bottom,
				rgba(var(--color-tertiary-100-rgb) / 0.4),
				rgba(var(--color-background-900-rgb) / 0.4)
			),
			rgba(0, 0, 0, 0.7);
		backdrop-filter: blur(30px);
	}

	div.overlay-backdrop.backdrop-flat-blur-mobile {
		opacity: 1;
		background:
			linear-gradient(var(--color-background-500), var(--color-background-500)), rgba(0, 0, 0, 0.7);
		backdrop-filter: blur(64px);
	}

	/*
	 * iOS pans the visual viewport within the (unchanged) layout viewport to keep a
	 * focused input above the keyboard. `.overlay` (and its `.overlay-backdrop` child)
	 * are deliberately left un-tracked and static here — already sized to the full
	 * layout viewport (h-full), which is taller than any keyboard-induced pan, so they
	 * stay a full-bleed cover with no repositioning needed. Moving a backdrop-filter
	 * element's geometry on every viewport event caused visible flicker/juddering on
	 * iOS, so only the plain (non-blurred) `.modal-container` wrapper is translated to
	 * keep the actual modal content glued above the keyboard.
	 */
	div.modal-container.vv-offset-tracked {
		transform: translateY(var(--modal-vv-offset, 0px));
	}

	/*
	 * Freezes the page at its current scroll position while a modal is open (see
	 * lockBodyScroll/unlockBodyScroll in ./utils/scroll-lock.ts). `:global` is needed
	 * since `html`/`body` aren't elements in this component's own template.
	 */
	:global(html.scroll-locked) {
		overflow: hidden;
	}

	:global(html.scroll-locked body) {
		position: fixed;
		top: var(--scroll-lock-offset, 0px);
		width: 100%;
	}

	@media (min-width: 1024px) {
		div.overlay {
				align-items: center;
		}
	}

	div.center {
		position: relative;
		width: 100%;
		background-image: var(--gradient-main);
}

	@media (min-width: 1024px) {
		div.center {
				border-radius: 24px;
		}
	}

	div.center-glass {
		background-image: unset;
	}

	div.center-blur {
		background-image: unset;
	}

	div.modal {
		position: relative;
		z-index: 100;
		display: flex;
		height: 100%;
		align-items: flex-start;
		border-width: 1.5px;
		border-color: var(--color-background-300);
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-100);
		--tw-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		font-family: var(--font-main);
		background-image:
			radial-gradient(circle at 50% 0%, var(--light-color), transparent 50%),
			radial-gradient(circle at 50% 100%, var(--light-color), transparent 50%),
			linear-gradient(to bottom, var(--color-background-700), var(--color-background-700)),
			linear-gradient(to bottom, rgba(255, 255, 255, 0.22), var(--color-neutral-900));
		background-origin: border-box;
		background-clip: content-box, border-box;
		border: double 1px transparent;
		box-shadow: 0 0 0 2px var(--color-neutral-900) !important;
		min-height: 100vh;
		min-height: 100dvh;
		min-height: var(--modal-vh, var(--app-vh, 100dvh));
}

	div.modal-flat {
		background-image: unset;
		background-color: var(--color-background-500);
	}

	div.modal-flat.modal-container-blur {
		background: var(--color-background-500);
		background-image: unset;
		border: none !important;
		box-shadow: none !important;
		backdrop-filter: blur(64px);
	}

	div.modal-tinted {
		border-width: 0px;
		background-color: rgb(from var(--color-secondary-500) r g b / 0.8);
		box-shadow: unset !important;
}

	div.modal-glass {
		--light-color: transparent;
		background: linear-gradient(
			to bottom,
			rgba(var(--color-tertiary-100-rgb) / 0.4),
			rgba(var(--color-background-900-rgb) / 0.4)
		);
		position: relative;
		border: none !important;
		backdrop-filter: blur(30px);
		background-clip: padding-box !important;
		background-origin: padding-box !important;
		overflow: hidden;
		box-shadow: none !important;
	}

	div.modal-glass::before {
		content: '';
		position: absolute;
		inset: 0;
		padding: 1px;
		background: linear-gradient(
			180deg,
			rgba(var(--color-secondary-300-rgb) / 0.3) 0%,
			rgba(255, 255, 255, 0) 100%
		);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
	}

	div.modal-glass > * {
		position: relative;
		z-index: 2;
	}

	/*
	 * On phone, the surface/blur has moved to the static `.overlay-backdrop` (see above) —
	 * the panel itself goes transparent so it doesn't also blur (and double-cost) on every
	 * keyboard resize. `!important` matches the specificity `.modal-glass` /
	 * `.modal-flat.modal-container-blur` already use for their own border/box-shadow.
	 */
	div.modal.modal-surface-on-backdrop {
		background: transparent !important;
		background-image: none !important;
		backdrop-filter: none !important;
		border: none !important;
		box-shadow: none !important;
	}

	div.modal.modal-surface-on-backdrop::before {
		display: none;
	}

	div.modal-purple {
		background-image: unset !important;
		background: var(--gradient-secondary) !important;
	}

	@media (min-width: 1024px) {
		div.modal {
				border-radius: 24px;
			min-height: 0;
		}

		div.modal-flat.modal-container-blur {
				border-radius: 12px;
		}

		div.modal-glass {
				border-radius: 16px;
		}

		div.modal-glass::before {
				border-radius: 16px;
		}
	}

	div.not-full-mobile-modal {
		min-height: 0;
	}

	div.modal-dark {
		background-image:
			radial-gradient(circle at 50% 0%, var(--light-color), transparent 50%),
			radial-gradient(circle at 50% 100%, var(--light-color), transparent 50%),
			linear-gradient(to bottom, var(--color-background-900), var(--color-background-900)),
			linear-gradient(to bottom, rgba(255, 255, 255, 0.22), rgba(48, 60, 105, 1));
	}

	div.center.centered,
	div.modal.centered {
		border-bottom-right-radius: 24px;
		border-bottom-left-radius: 24px;
		border-top-left-radius: 24px;
		border-top-right-radius: 24px;
}

	div.overlay.centered {
		align-items: center;
}

	@media (max-width: 1023px) {
		div.overlay.full-vh {
			align-items: flex-start !important;
		}
	}

	div.modal-container {
		position: relative;
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		max-height: 100%;
}

	div.modal-container-blur {
		border-width: 0px !important;
		backdrop-filter: blur(64px);
}

	div.content-wrapper-blur {
		border-radius: 24px;
}

	div.content-wrapper-blur.content-wrapper-flat-blur {
		border-radius: 12px;
}

	div.modal.mobile-modal {
		animation: slideUp 0.5s ease-out;
	}

	div.center.mobile-center-animation {
		animation: slideUp 0.5s ease-out;
	}

	div.center.desktop-center-animation {
		animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
	}

	div.content-wrapper {
		z-index: 40;
		display: flex;
		height: 100%;
		max-height: 100%;
		width: 100%;
		flex-direction: column;
		justify-content: flex-start;
		overflow: auto;
		padding: 1rem;
}

	@media (min-width: 1024px) {
		div.content-wrapper {
				padding: 1.5rem;
		}
	}

	div.content {
		flex-grow: 1;
		overflow-y: auto;
		padding: 0 0.2rem 0 0.2rem;
}

	div.content::-webkit-scrollbar {
		width: 0px;
}

	div.content-with-scrollbar::-webkit-scrollbar {
		width: 0.25rem;
}

	div.content::-webkit-scrollbar-track {
		border-radius: 0.5rem;
		background-color: var(--color-background-300);
		padding: 0px;
}

	div.content::-webkit-scrollbar-thumb {
		border-radius: 0.5rem;
		background-color: var(--color-neutral-900);
		--tw-shadow-color: var(--shadow-neutral);
		--tw-shadow: var(--tw-shadow-colored);
}

	div.content::-webkit-scrollbar-thumb:hover {
		background-color: var(--color-neutral-700);
}

	button.close {
		position: absolute;
		top: 0px;
		right: 0px;
		z-index: 150;
		margin: 1rem;
		display: flex;
		height: 24px;
		width: 24px;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
		border-top-width: 1px;
		border-color: #3F4B7980;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 500;
		--tw-text-opacity: 1;
		color: rgb(209 213 219 / var(--tw-text-opacity, 1));
}

	button.close-flat {
		border-width: 0px;
		background-image: unset;
}

	button.close-glass {
		border-radius: 4px;
		border-width: 0px;
		background: radial-gradient(
			100% 100% at 0% 50%,
			var(--color-tertiary-300) 0%,
			var(--color-tertiary-700) 100%
		);
}

	button.close-glass::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		padding: 1px;
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
	}

	button.close:hover {
		--tw-border-opacity: 1;
		border-color: rgb(156 163 175 / var(--tw-border-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(229 231 235 / var(--tw-text-opacity, 1));
}

	h2 {
		font-size: 1.25rem;
		line-height: 1.75rem;
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
}

	.title-container {
		margin-top: 0.25rem;
		margin-bottom: 1rem;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
}

	@media (min-width: 1024px) {
		.title-container {
				justify-content: flex-start;
		}

		h2 {
				font-size: 1.5rem;
				line-height: 2rem;
		}
	}

	.close-button-container {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 1rem;
}

	.mobile-center {
		max-width: 100vw;
}

	.with-background {
		height: 24px;
		width: 24px;
		border-radius: 8px;
		background-image: linear-gradient(
			180deg,
			var(--color-background-300) 0%,
			var(--color-background-700) 100%
		);
}
</style>
